import { Goto } from "@/shared/navigation";

/**
 * remark plugin that rewrites the user mentions a person types in a comment
 * into links to each mentioned user's page.
 *
 * Surface form:                 `@[Display Name](user_id)`
 * Rewritten link target:        the page of `user_id`
 *                               (`Goto.createUserRoute`, which encodes it)
 * Rewritten link text:          `@Display Name`
 *
 * By the time this plugin runs, remark has already parsed
 * `[Display Name](user_id)` as a markdown link. The AST therefore looks like
 * `[ text("hi @"), link(url="user_id", children=[…]), … ]`, not a single
 * text node containing the literal `@[…](…)` source. The walker matches that
 * split shape: a `link` whose immediately-preceding sibling is a text node
 * ending in `@`. The comment input writes the user id into the destination
 * as is, not percent-encoded, so `link.url` is the user id itself.
 *
 * The `@` moves INSIDE the link so the `@` and the name render as one link,
 * with one underline and one hover state, instead of a bare `@` beside it.
 *
 * The walker does not descend into code (fenced or inline) or links, so a
 * `@[name](id)` literal in a code sample passes through unchanged.
 */

interface MdastNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
}

function rewriteLinkAsMention(prevText: MdastNode, link: MdastNode): void {
  // The trailing `@` moves from the preceding text node into the link text.
  prevText.value = prevText.value!.slice(0, -1);

  link.url = Goto.createUserRoute(link.url ?? "");

  // Prepend `@` to the link's existing children so emphasis / inline code
  // inside the display name survives.
  const first = link.children?.[0];
  if (first && first.type === "text" && first.value !== undefined) {
    first.value = `@${first.value}`;
  } else {
    link.children = [{ type: "text", value: "@" }, ...(link.children ?? [])];
  }
}

function walk(node: MdastNode): void {
  // Don't descend into code blocks (a `@[name](id)` literal inside code
  // should pass through verbatim) or into existing links (avoids double-
  // rewriting nested patterns).
  if (
    node.type === "code" ||
    node.type === "inlineCode" ||
    node.type === "link"
  ) {
    return;
  }
  if (!node.children) {
    return;
  }

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    if (child.type === "link" && i > 0) {
      const prev = node.children[i - 1];
      if (
        prev.type === "text" &&
        prev.value !== undefined &&
        prev.value.endsWith("@")
      ) {
        rewriteLinkAsMention(prev, child);
        // The link was just rewritten; don't descend into it.
        continue;
      }
    }
    walk(child);
  }
}

export function remarkUserMentions() {
  return (tree: MdastNode, _file?: unknown) => {
    walk(tree);
  };
}
